/**
 * RecordPreviewSheet - Shows full details of a DI record in a side sheet
 * Displays UUID, ALL fields (even empty ones), and has Edit button
 */

import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Building2,
  User,
  MapPin,
  Zap,
  Link2,
  FileText,
  Home,
  Edit2,
  Copy,
} from 'lucide-react';
import { formatDate, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';

const RECORD_ICONS = {
  organisation: Building2,
  person: User,
  location: MapPin,
  property: Home,
  event: Zap,
  relationship: Link2,
  document: FileText,
};

const RECORD_COLORS = {
  organisation: 'text-blue-400',
  person: 'text-green-400',
  location: 'text-amber-400',
  property: 'text-orange-400',
  event: 'text-purple-400',
  relationship: 'text-pink-400',
  document: 'text-teal-400',
};

// Field definitions for each record type - ALL fields that should be shown
const FIELD_DEFINITIONS = {
  organisation: [
    { key: 'companies_house_number', label: 'Companies House Number', mono: true, copyable: true },
    { key: 'company_type', label: 'Company Type' },
    { key: 'status', label: 'Status' },
    { key: 'sector', label: 'Sector' },
    { key: 'subsector', label: 'Subsector' },
    { key: 'jurisdiction', label: 'Jurisdiction' },
    { key: 'incorporation_date', label: 'Incorporation Date', isDate: true },
    { key: 'website', label: 'Website' },
    { key: 'vat_number', label: 'VAT Number', mono: true },
    { key: 'charity_number', label: 'Charity Number', mono: true },
    { key: 'address', label: 'Address', isAddress: true },
    { key: 'postcode', label: 'Postcode' },
    { key: 'postcode_area', label: 'Postcode Area' },
    { key: 'local_authority', label: 'Local Authority' },
    { key: 'region', label: 'Region' },
    { key: 'country', label: 'Country' },
    { key: 'lat', label: 'Latitude', mono: true },
    { key: 'lng', label: 'Longitude', mono: true },
    { key: 'insolvency_flag', label: 'Insolvency Flag', isBool: true },
    { key: 'insolvency_type', label: 'Insolvency Type' },
    { key: 'charges_count', label: 'Charges Count' },
    { key: 'accounts_overdue', label: 'Accounts Overdue', isBool: true },
    { key: 'regulatory_action_flag', label: 'Regulatory Action', isBool: true },
    { key: 'litigation_flag', label: 'Litigation Flag', isBool: true },
    { key: 'description', label: 'Description' },
    { key: 'notes', label: 'Notes' },
  ],
  person: [
    { key: 'person_type', label: 'Person Type' },
    { key: 'title', label: 'Title' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'job_title', label: 'Job Title' },
    { key: 'seniority_level', label: 'Seniority Level' },
    { key: 'sector', label: 'Sector' },
    { key: 'organisation_name', label: 'Organisation' },
    { key: 'organisation_id', label: 'Organisation ID', mono: true },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'linkedin_url', label: 'LinkedIn URL' },
    { key: 'address', label: 'Address', isAddress: true },
    { key: 'postcode', label: 'Postcode' },
    { key: 'town_city', label: 'Town/City' },
    { key: 'county', label: 'County' },
    { key: 'country', label: 'Country' },
    { key: 'date_of_birth', label: 'Date of Birth', isDate: true },
    { key: 'nationality', label: 'Nationality' },
    { key: 'biography', label: 'Biography' },
    { key: 'notes', label: 'Notes' },
  ],
  location: [
    { key: 'address', label: 'Address', isAddress: true },
    { key: 'address_line1', label: 'Address Line 1' },
    { key: 'address_line2', label: 'Address Line 2' },
    { key: 'town_city', label: 'Town/City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'postcode_area', label: 'Postcode Area' },
    { key: 'local_authority', label: 'Local Authority' },
    { key: 'region', label: 'Region' },
    { key: 'country', label: 'Country' },
    { key: 'parliamentary_constituency', label: 'Constituency' },
    { key: 'lsoa_name', label: 'LSOA Name' },
    { key: 'lsoa_code', label: 'LSOA Code', mono: true },
    { key: 'msoa_code', label: 'MSOA Code', mono: true },
    { key: 'lat', label: 'Latitude', mono: true },
    { key: 'lng', label: 'Longitude', mono: true },
    { key: 'description', label: 'Description' },
    { key: 'notes', label: 'Notes' },
  ],
  property: [
    { key: 'property_type', label: 'Property Type' },
    { key: 'tenure', label: 'Tenure' },
    { key: 'address', label: 'Address', isAddress: true },
    { key: 'address_line1', label: 'Address Line 1' },
    { key: 'address_line2', label: 'Address Line 2' },
    { key: 'town_city', label: 'Town/City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'country', label: 'Country' },
    { key: 'title_number', label: 'Title Number', mono: true, copyable: true },
    { key: 'uprn', label: 'UPRN', mono: true, copyable: true },
    { key: 'floor_area_sqft', label: 'Floor Area (sq ft)' },
    { key: 'land_area_acres', label: 'Land Area (acres)' },
    { key: 'year_built', label: 'Year Built' },
    { key: 'epc_rating', label: 'EPC Rating' },
    { key: 'estimated_value', label: 'Estimated Value', isCurrency: true },
    { key: 'last_sale_price', label: 'Last Sale Price', isCurrency: true },
    { key: 'last_sale_date', label: 'Last Sale Date', isDate: true },
    { key: 'owner_organisation_name', label: 'Owner Organisation' },
    { key: 'owner_organisation_id', label: 'Owner Organisation ID', mono: true },
    { key: 'owner_person_name', label: 'Owner Person' },
    { key: 'lat', label: 'Latitude', mono: true },
    { key: 'lng', label: 'Longitude', mono: true },
    { key: 'description', label: 'Description' },
    { key: 'notes', label: 'Notes' },
  ],
  event: [
    { key: 'event_category', label: 'Event Category' },
    { key: 'event_type', label: 'Event Type' },
    { key: 'event_date', label: 'Event Date', isDate: true },
    { key: 'effective_date', label: 'Effective Date', isDate: true },
    { key: 'end_date', label: 'End Date', isDate: true },
    { key: 'is_ongoing', label: 'Ongoing', isBool: true },
    { key: 'related_organisation_name', label: 'Related Organisation' },
    { key: 'related_organisation_id', label: 'Related Organisation ID', mono: true },
    { key: 'related_person_name', label: 'Related Person' },
    { key: 'related_person_id', label: 'Related Person ID', mono: true },
    { key: 'related_property_id', label: 'Related Property ID', mono: true },
    { key: 'source_url', label: 'Source URL' },
    { key: 'gazette_reference', label: 'Gazette Reference', mono: true },
    { key: 'description', label: 'Description' },
    // Insolvency fields
    { key: 'insolvency.practitioner_name', label: 'Insolvency Practitioner', nested: 'insolvency' },
    { key: 'insolvency.practitioner_firm', label: 'Practitioner Firm', nested: 'insolvency' },
    { key: 'insolvency.case_number', label: 'Case Number', mono: true, nested: 'insolvency' },
    { key: 'insolvency.court_name', label: 'Court Name', nested: 'insolvency' },
    // Procurement fields
    { key: 'procurement.contract_value', label: 'Contract Value', isCurrency: true, nested: 'procurement' },
    { key: 'procurement.contract_reference', label: 'Contract Reference', mono: true, nested: 'procurement' },
    { key: 'procurement.buyer_name', label: 'Buyer Name', nested: 'procurement' },
    { key: 'procurement.currency', label: 'Currency', nested: 'procurement' },
    // Regulatory fields
    { key: 'regulatory.regulator_name', label: 'Regulator Name', nested: 'regulatory' },
    { key: 'regulatory.reference_number', label: 'Reference Number', mono: true, nested: 'regulatory' },
    { key: 'regulatory.fine_amount', label: 'Fine Amount', isCurrency: true, nested: 'regulatory' },
    { key: 'regulatory.breach_type', label: 'Breach Type', nested: 'regulatory' },
    { key: 'notes', label: 'Notes' },
  ],
  relationship: [
    { key: 'relationship_type', label: 'Relationship Type' },
    { key: 'from_entity_name', label: 'From Entity' },
    { key: 'from_entity_type', label: 'From Entity Type' },
    { key: 'from_entity_id', label: 'From Entity ID', mono: true },
    { key: 'to_entity_name', label: 'To Entity' },
    { key: 'to_entity_type', label: 'To Entity Type' },
    { key: 'to_entity_id', label: 'To Entity ID', mono: true },
    { key: 'start_date', label: 'Start Date', isDate: true },
    { key: 'end_date', label: 'End Date', isDate: true },
    { key: 'is_current', label: 'Currently Active', isBool: true },
    { key: 'sector', label: 'Sector' },
    { key: 'role_title', label: 'Role/Title' },
    { key: 'shareholding_percentage', label: 'Shareholding %' },
    { key: 'investment_amount', label: 'Investment Amount', isCurrency: true },
    { key: 'description', label: 'Description' },
    { key: 'notes', label: 'Notes' },
  ],
  document: [
    { key: 'document_type', label: 'Document Type' },
    { key: 'file_name', label: 'File Name' },
    { key: 'file_type', label: 'File Type' },
    { key: 'file_size', label: 'File Size', isFileSize: true },
    { key: 'file_path', label: 'File Path', mono: true },
    { key: 'file_url', label: 'File URL' },
    { key: 'publication_date', label: 'Publication Date', isDate: true },
    { key: 'author', label: 'Author' },
    { key: 'publisher', label: 'Publisher' },
    { key: 'reference_number', label: 'Reference Number', mono: true },
    { key: 'related_organisation_name', label: 'Related Organisation' },
    { key: 'related_organisation_id', label: 'Related Organisation ID', mono: true },
    { key: 'related_person_name', label: 'Related Person' },
    { key: 'related_event_id', label: 'Related Event ID', mono: true },
    { key: 'extraction_status', label: 'Extraction Status' },
    { key: 'extracted_text', label: 'Extracted Text' },
    { key: 'page_count', label: 'Page Count' },
    { key: 'description', label: 'Description' },
    { key: 'notes', label: 'Notes' },
  ],
};

function DetailRow({ label, value, mono = false, copyable = false, showEmpty = true }) {
  const isEmpty = value === null || value === undefined || value === '';
  
  if (isEmpty && !showEmpty) return null;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(String(value));
    toast.success('Copied to clipboard');
  };
  
  return (
    <div className="py-2 border-b border-border-default last:border-0">
      <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">{label}</div>
      <div className={cn(
        "flex items-center gap-2",
        mono && "font-mono text-sm",
        isEmpty ? "text-text-tertiary italic" : "text-text-primary"
      )}>
        <span className="flex-1 break-words">
          {isEmpty ? '—' : String(value)}
        </span>
        {copyable && !isEmpty && (
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCopy}>
            <Copy className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function getNestedValue(obj, key, nestedKey) {
  if (nestedKey && obj[nestedKey]) {
    const simpleKey = key.split('.').pop();
    return obj[nestedKey][simpleKey];
  }
  return obj[key];
}

/**
 * Search for a value in the content object, checking nested paths
 * Data structure: content.identity.*, content.address.*, content.risk.*, content.links.*
 */
function findValueInContent(content, key) {
  if (!content) return null;
  
  // Direct key on content
  if (content[key] !== undefined) return content[key];
  
  // Check in identity section
  if (content.identity && content.identity[key] !== undefined) {
    return content.identity[key];
  }
  
  // Check in address section
  if (content.address && content.address[key] !== undefined) {
    return content.address[key];
  }
  
  // Check in risk section
  if (content.risk && content.risk[key] !== undefined) {
    return content.risk[key];
  }
  
  // Check in links section
  if (content.links && content.links[key] !== undefined) {
    return content.links[key];
  }
  
  // Check in location section (for events)
  if (content.location && content.location[key] !== undefined) {
    return content.location[key];
  }
  
  // Check in entities section (for relationships)
  if (content.entities && content.entities[key] !== undefined) {
    return content.entities[key];
  }
  
  // Check in extraction section (for documents)
  if (content.extraction && content.extraction[key] !== undefined) {
    return content.extraction[key];
  }
  
  return null;
}

function formatFieldValue(value, field, content) {
  if (value === null || value === undefined || value === '') return null;
  
  if (field.isDate) {
    return formatDate(value);
  }
  if (field.isBool) {
    return value ? 'Yes' : 'No';
  }
  if (field.isCurrency) {
    return `£${Number(value).toLocaleString()}`;
  }
  if (field.isFileSize) {
    return `${Math.round(value / 1024)} KB`;
  }
  if (field.isAddress) {
    const lines = [
      content?.address_line1,
      content?.address_line2,
      content?.town_city,
      content?.county,
      content?.postcode,
      content?.country,
    ].filter(Boolean);
    return lines.length > 0 ? lines.join(', ') : null;
  }
  
  return value;
}

function TagsList({ tags }) {
  const hasTags = tags && tags.length > 0;
  
  return (
    <div className="py-2 border-b border-border-default">
      <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Tags</div>
      {hasTags ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      ) : (
        <div className="text-text-tertiary italic">—</div>
      )}
    </div>
  );
}

function ConfidenceBadge({ score }) {
  if (!score && score !== 0) return null;
  
  const percentage = Math.round(score * 100);
  const colorClass = percentage >= 80 ? 'text-green-400' : 
                     percentage >= 60 ? 'text-amber-400' : 'text-red-400';
  
  return (
    <Badge className={cn("border", colorClass)}>
      {percentage}% confidence
    </Badge>
  );
}

export function RecordPreviewSheet({ record, open, onOpenChange, onEdit }) {
  if (!record) return null;
  
  const Icon = RECORD_ICONS[record.record_type] || Building2;
  const iconColor = RECORD_COLORS[record.record_type] || 'text-blue-400';
  const content = record.content || {};
  const fields = FIELD_DEFINITIONS[record.record_type] || [];
  
  const handleCopyUUID = () => {
    navigator.clipboard.writeText(record.id);
    toast.success('UUID copied to clipboard');
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg bg-surface-card border-border-default">
        <SheetHeader className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg bg-bg-tertiary", iconColor)}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <SheetTitle className="text-text-primary text-lg">
                  {record.title}
                </SheetTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs capitalize">
                    {record.record_type}
                  </Badge>
                  <ConfidenceBadge score={content.confidence_score} />
                </div>
              </div>
            </div>
          </div>
          
          {/* UUID Section */}
          <div className="bg-bg-tertiary rounded-lg p-3">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Record UUID</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono text-accent-primary break-all">
                {record.id}
              </code>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handleCopyUUID}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>
        
        <Separator className="my-4" />
        
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="space-y-1 pr-4">
            {/* Common Fields */}
            <DetailRow label="Source" value={record.source} />
            <DetailRow label="Source URL" value={content.source_url} />
            <DetailRow label="Created" value={formatDateTime(record.created_at)} />
            <DetailRow label="Updated" value={formatDateTime(record.updated_at)} />
            
            <Separator className="my-4" />
            
            {/* Dynamic Fields Based on Record Type */}
            {fields.map((field) => {
              // Use findValueInContent to search nested paths
              const rawValue = field.nested 
                ? getNestedValue(content, field.key, field.nested)
                : findValueInContent(content, field.key);
              const formattedValue = formatFieldValue(rawValue, field, content);
              
              // Skip address field if it would be empty (it's a composite field)
              if (field.isAddress && !formattedValue) {
                return (
                  <DetailRow 
                    key={field.key} 
                    label={field.label} 
                    value={null}
                    mono={field.mono}
                    copyable={field.copyable}
                    showEmpty={true}
                  />
                );
              }
              
              return (
                <DetailRow 
                  key={field.key} 
                  label={field.label} 
                  value={formattedValue}
                  mono={field.mono}
                  copyable={field.copyable}
                  showEmpty={true}
                />
              );
            })}
            
            <Separator className="my-4" />
            
            {/* Tags */}
            <TagsList tags={record.tags} />
            
            {/* Confidence Score */}
            <DetailRow label="Confidence Score" value={content.confidence_score ? `${Math.round(content.confidence_score * 100)}%` : null} />
            
            {/* Raw Content (collapsible) */}
            <details className="mt-4">
              <summary className="text-xs text-text-tertiary uppercase tracking-wider cursor-pointer hover:text-text-secondary">
                Raw JSON Content
              </summary>
              <pre className="mt-2 p-3 bg-bg-tertiary rounded-lg text-xs font-mono overflow-auto max-h-40">
                {JSON.stringify(content, null, 2)}
              </pre>
            </details>
          </div>
        </ScrollArea>
        
        <Separator className="my-4" />
        
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => onEdit?.(record)} data-testid="edit-record-btn">
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Record
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default RecordPreviewSheet;
